import { type FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import * as Sentry from '@sentry/react'
import { Button } from '../../../../../components/Button/Button'
import { type AccountSchedulingSettings, type Location, MeetingMode, type SchedulingEventType } from '../../../../../models/Scheduling'
import { BOOKING_BASE_URL } from '../../../../Scheduling/shared/bookingBaseUrl'
import { EditEventTypeModal } from '../EditEventTypeModal'

interface EventTypeSummaryCardProps {
  draft: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  locations: Location[]
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
}

export const EventTypeSummaryCard: FC<EventTypeSummaryCardProps> = ({ draft, accountSettings, locations, disabled, onDraftChange }) => {
  const [isEditing, setIsEditing] = useState(false)

  const accountSlug = accountSettings?.publicBookingPage?.accountSlug

  useEffect(() => {
    if (accountSettings && !accountSlug) {
      Sentry.captureException(new Error('Scheduling: account is missing accountSlug; cannot build a booking URL in the event type summary card'))
    }
  }, [accountSettings, accountSlug])

  return (
    <div className='d-flex flex-column gap-2 p-3 border rounded-3 zocks-border-color-gray-300 zocks-bg-color-white'>
      <div className='d-flex align-items-start justify-content-between gap-2'>
        <span className='bold paragraph-large'>{draft.name}</span>
        {!disabled ? (
          <Button variant='grayButton' className='p-2' aria-label='Edit event type' onClick={() => setIsEditing(true)}>
            <i className='bi bi-pencil' />
          </Button>
        ) : null}
      </div>
      <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small zocks-text-gray-900'>
        <span>{draft.durationMinutes} minutes</span>
        <span>·</span>
        <span className='d-inline-flex align-items-center zocks-gap-0.25'>
          <i className={draft.meetingMode === MeetingMode.VIRTUAL ? 'bi bi-camera-video' : 'bi bi-people'} />
          {draft.meetingMode === MeetingMode.VIRTUAL ? 'Virtual' : 'In person'}
        </span>
      </div>
      {draft.description ? <div className='paragraph-small zocks-text-gray-700'>{draft.description}</div> : null}
      <div className={classNames('d-flex align-items-center zocks-gap-0.5 paragraph-small', accountSlug ? 'zocks-text-gray-600' : 'zocks-text-danger')}>
        <i className='bi bi-link-45deg flex-shrink-0' />
        <span className='text-truncate'>{accountSlug ? `${BOOKING_BASE_URL}/${accountSlug}/.../${draft.slug}` : 'Booking URL unavailable'}</span>
      </div>
      {isEditing ? (
        <EditEventTypeModal
          eventType={draft}
          accountSettings={accountSettings}
          locations={locations}
          onSave={(patch) => {
            onDraftChange(patch)
            setIsEditing(false)
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : null}
    </div>
  )
}
